/**
 * Crew (pure): who is on the shoot, what they do, which camera they run and where they are posted.
 *
 * Two separate ideas, kept apart everywhere in the UI:
 *  - Job (`CrewJob`): what someone does on the shoot — Producer, Camera op, Pilot (chase)…
 *  - App access (`Role`): what they may change in Airview Studio — owner / producer / crew / viewer.
 *
 * `crewPeople` reads a bundle defensively: an older server sends no `people`, so members and the
 * names typed on cameras (round 3 `operatorName`) become read-only "virtual" people.
 */
import type { Camera, CrewJob, Id, MissionBundle, Person } from './types.ts';

export const CREW_JOBS: ReadonlyArray<{ value: CrewJob; label: string }> = [
  { value: 'producer', label: 'Producer' },
  { value: 'director', label: 'Director' },
  { value: 'camera_op', label: 'Camera op' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'drone_op', label: 'Drone op' },
  { value: 'chase_pilot', label: 'Pilot (chase)' },
  { value: 'audio', label: 'Audio' },
  { value: 'pa', label: 'PA' },
  { value: 'other', label: 'Other' },
];

export const CREW_JOB_LABEL: Record<CrewJob, string> = Object.fromEntries(CREW_JOBS.map((j) => [j.value, j.label])) as Record<CrewJob, string>;

export const isCrewJob = (v: unknown): v is CrewJob => typeof v === 'string' && v in CREW_JOB_LABEL;

/** A person as the UI sees them; `virtual` = synthesised from older data (not editable). */
export type CrewPerson = Person & { virtual?: boolean };

type CameraLike = Pick<Camera, 'id' | 'operatorUserId'> & { operatorName?: string | null; personId?: Id | null };

const nameKey = (s: string) => s.trim().toLowerCase();

/** Everyone on the shoot: stored people, plus members / typed operator names an older server didn't send. */
export function crewPeople(bundle: Pick<MissionBundle, 'mission' | 'members' | 'cameras'> & { people?: Person[] }): CrewPerson[] {
  const out: CrewPerson[] = [...(bundle.people ?? [])];
  const byUser = new Set(out.flatMap((p) => (p.userId ? [p.userId] : [])));
  const byName = new Set(out.filter((p) => !p.userId).map((p) => nameKey(p.name)));
  const missionId = bundle.mission.id;
  for (const m of bundle.members) {
    if (byUser.has(m.userId)) continue;
    out.push({ id: `member:${m.userId}`, missionId, userId: m.userId, name: m.displayName || m.callsign, phone: null, job: m.role === 'owner' ? 'producer' : null, locationId: null, notes: '', createdAt: m.joinedAt, updatedAt: m.joinedAt, virtual: true });
    byUser.add(m.userId);
  }
  for (const c of bundle.cameras as CameraLike[]) {
    const n = c.operatorName?.trim();
    if (!n || c.operatorUserId || (c.personId && out.some((p) => p.id === c.personId)) || byName.has(nameKey(n))) continue;
    out.push({ id: `name:${nameKey(n)}`, missionId, userId: null, name: n, phone: null, job: 'camera_op', locationId: null, notes: '', createdAt: 0, updatedAt: 0, virtual: true });
    byName.add(nameKey(n));
  }
  return out;
}

/** The person who runs a camera: its personId, else the member operating it, else the typed name. */
export function cameraPerson(cam: CameraLike, people: readonly CrewPerson[]): CrewPerson | null {
  if (cam.personId) {
    const p = people.find((x) => x.id === cam.personId);
    if (p) return p;
  }
  if (cam.operatorUserId) return people.find((x) => x.userId === cam.operatorUserId) ?? null;
  const n = cam.operatorName?.trim();
  return n ? (people.find((x) => !x.userId && nameKey(x.name) === nameKey(n)) ?? null) : null;
}

/** Cameras this person runs, in bundle order. */
export function personCameras<C extends CameraLike>(person: Pick<Person, 'id'>, cameras: readonly C[], people: readonly CrewPerson[]): C[] {
  return cameras.filter((c) => cameraPerson(c, people)?.id === person.id);
}

/** Zoom range "85–200 mm" (lane FOV's focalMinMm/focalMaxMm when present) or a prime "50 mm". */
export function lensText(cam: Pick<Camera, 'focalLengthMm'> & { focalMinMm?: number | null; focalMaxMm?: number | null }): string | null {
  const lo = cam.focalMinMm ?? null;
  const hi = cam.focalMaxMm ?? null;
  if (lo && hi && hi > lo) return `${Math.round(lo)}–${Math.round(hi)} mm`;
  const f = cam.focalLengthMm ?? lo ?? hi;
  return f ? `${Math.round(f)} mm` : null;
}

/**
 * One line for "You: …" — like a call sheet: "CAM B · CAM SPOT N · 85–200 mm" (one camera), or
 * "CAM B, CAM C · CAM SPOT N" (several). Null when the person has no camera and no place.
 */
export function assignmentLine(
  person: Pick<Person, 'id' | 'locationId'>,
  bundle: Pick<MissionBundle, 'cameras' | 'locations'>,
  people: readonly CrewPerson[],
): string | null {
  const cams = personCameras(person, bundle.cameras, people);
  const place = person.locationId ? bundle.locations.find((l) => l.id === person.locationId) : undefined;
  const parts: string[] = [];
  if (cams.length) parts.push(cams.map((c) => c.label).join(', '));
  if (place) parts.push(place.name);
  const lens = cams.length === 1 ? lensText(cams[0]!) : null;
  if (lens) parts.push(lens);
  return parts.length ? parts.join(' · ') : null;
}
